/**
 * useLinks Composable
 *
 * Fetches the slim record_links rows for a given set of record ids — every
 * link where either endpoint is in `ids`. Endpoint records are NOT included;
 * resolve them client-side (see useLinkedRecords).
 *
 * The links query waits for `ids` to be populated, then refetches whenever
 * the id set actually changes (additions/removals — not on object identity).
 *
 * Large id sets are chunked into parallel queries to keep each Postgres
 * `IN (...)` clause small enough for the planner to handle cheaply. Results
 * are deduped by row id before being returned.
 *
 * @example
 * const records = useRecords({ query, variables }, deps)
 * const ids = computed(() => records.items.value.map((r) => r.id))
 * const { links, loading, error, refresh } = useLinks(
 *   { ids, excludeBacklinkTypes: ["document"] },
 *   deps
 * )
 */

import { type MaybeRefOrGetter, ref, shallowRef, toValue, watch } from "vue"
import type { RecordLinkFlatFieldsFragment } from "@auger/core/gql/types"
import { graphql } from "@/gql"
import { errorMessage } from "@/utils/errors"
import type { ComposableDeps } from "./types"

export interface LinksOptions {
  ids: MaybeRefOrGetter<number[] | null | undefined>
  excludeBacklinkTypes?: MaybeRefOrGetter<string[] | null | undefined>
}

// Flat record_links rows where either endpoint is in `ids`. The backlink leg
// (`linked_id` in ids — i.e. someone else linked to one of our records) also
// excludes incoming links from record types in `excludeBacklinkTypes`. This
// is a UX call: e.g. document → record backlinks dominate the payload but
// aren't surfaced in card-style views.
const fetchLinksForIds = graphql(`
  query fetchLinksForIds($ids: [Int!]!, $excludeBacklinkTypes: [String!]!) {
    record_links(
      where: {
        _or: [
          { record_id: { _in: $ids } }
          { linked_id: { _in: $ids }, record: { type: { _nin: $excludeBacklinkTypes } } }
        ]
      }
    ) {
      ...RecordLinkFlatFields
    }
  }
`)

// Postgres `IN (...)` planner cost grows fast with list length. Chunking at
// 500 keeps each chunk in the cheap range. Chunks run with bounded concurrency
// so a 10k-record type doesn't fan out 20 simultaneous queries and saturate
// the Hasura connection pool.
const ID_CHUNK_SIZE = 500
const CHUNK_CONCURRENCY = 5

// Run `fn` over `items` with at most `concurrency` in flight at once. Returns
// results in input order. Failures reject the whole batch (matches Promise.all).
async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  async function worker() {
    while (true) {
      const i = next++
      if (i >= items.length) return
      results[i] = await fn(items[i]!, i)
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, worker)
  await Promise.all(workers)
  return results
}

// Stable signature for an id list so the watcher only fires on actual set changes.
function idsSignature(ids: number[] | null | undefined) {
  if (!ids?.length) return ""
  return [...ids].sort((a, b) => a - b).join(",")
}

function chunk<T>(array: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < array.length; i += size) out.push(array.slice(i, i + size))
  return out
}

// Dedup record_links rows by id. A single row can be returned by two chunks
// when its record_id and linked_id fall into different chunks.
function dedupeById<T extends { id: number }>(rows: T[]): T[] {
  const seen = new Set<number>()
  const out: T[] = []
  for (const row of rows) {
    if (row?.id == null || seen.has(row.id)) continue
    seen.add(row.id)
    out.push(row)
  }
  return out
}

export function useLinks(options: LinksOptions, deps: ComposableDeps) {
  const { client, toast } = deps
  const { ids: idsOption, excludeBacklinkTypes = [] } = options

  if (!idsOption) {
    throw new Error("useLinks: ids is required")
  }

  const getIds = () => toValue(idsOption)
  const getExcludes = () => toValue(excludeBacklinkTypes)

  const links = shallowRef<RecordLinkFlatFieldsFragment[]>([])
  const loading = ref(false)
  const error = ref<unknown>(null)
  let fetchToken = 0

  function fetchLinks() {
    const ids = getIds() || []
    const excludeBacklinkTypes = getExcludes() || []
    const startToken = ++fetchToken

    // No ids yet: clear and short-circuit. Will re-run when ids populate.
    if (!ids.length) {
      links.value = []
      loading.value = false
      return Promise.resolve(links.value)
    }

    loading.value = true
    error.value = null

    const chunks = chunk(ids, ID_CHUNK_SIZE)

    return mapWithConcurrency(chunks, CHUNK_CONCURRENCY, (chunkIds) =>
      client.query({
        query: fetchLinksForIds,
        variables: { ids: chunkIds, excludeBacklinkTypes },
        fetchPolicy: "no-cache",
      })
    )
      .then((results) => {
        if (startToken !== fetchToken) return links.value
        const rows = results.flatMap(({ data }) => data?.record_links || [])
        // The `_or` clause can return the same row from two chunks (when its
        // record_id and linked_id fall into different chunks). Dedupe by id.
        links.value = dedupeById(rows)
        return links.value
      })
      .catch((err) => {
        const message = errorMessage(err)
        if (startToken !== fetchToken) return
        error.value = err
        toast?.error?.(`Failed to load links: ${message}`)
        throw err
      })
      .finally(() => {
        if (startToken === fetchToken) loading.value = false
      })
  }

  fetchLinks()

  watch(
    () => `${idsSignature(getIds())}|${(getExcludes() || []).join(",")}`,
    () => {
      fetchLinks()
    }
  )

  return {
    links,
    loading,
    error,
    refresh: fetchLinks,
  }
}
